import {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import { isEqual } from 'lodash'
import { type Virtualizer, useVirtualizer } from '@tanstack/react-virtual'
import { getAriaLabel } from 'shared/lib'
import { type IScrollAnchor, resolveScrollAnchor } from '../lib/resolveScrollAnchor'
import { type IVirtualListProps, type IVirtualListRef } from './types'
import styles from './styles.module.scss'

export const VirtualList = forwardRef<IVirtualListRef, IVirtualListProps>(
  (
    {
      children,
      estimateSize = 40,
      overscan = 8,
      gap,
      paddingStart,
      paddingEnd,
      onEndReached,
      endReachedThreshold = 5,
      onStartReached,
      startReachedThreshold = 5,
      anchorTo,
      followOnAppend,
      scrollEndThreshold,
      directDomUpdates,
      flushSyncOnScroll = true,
      useAnimationFrameWithResizeObserver = false,
      measureItems = true,
      skeletonWhileScrolling = false,
      renderSkeleton,
      header,
      footer,
      onScroll,
      className,
      contentWrapperClassName,
      contentClassName,
      style,
      scrollElement,
      ariaLabel,
    },
    ref
  ) => {
    const ownScrollRef = useRef<HTMLDivElement>(null)
    const externalScroll = scrollElement !== undefined
    const getScrollElement = useCallback(
      () => (externalScroll ? (scrollElement as HTMLDivElement | null) : ownScrollRef.current),
      [externalScroll, scrollElement]
    )
    const headerRef = useRef<HTMLDivElement>(null)
    const [headerHeight, setHeaderHeight] = useState(0)
    const items = useMemo(() => Children.toArray(children), [children])

    const resolveSize = useCallback(
      (index: number) => (typeof estimateSize === 'function' ? estimateSize(index) : estimateSize),
      [estimateSize]
    )

    const nextKeys = items.map((child, index) =>
      isValidElement(child) && child.key !== null ? child.key : index
    )
    const keysRef = useRef<Array<string | number>>(nextKeys)
    const virtualizerRef = useRef<Virtualizer<HTMLDivElement, Element> | null>(null)
    const anchorRef = useRef<IScrollAnchor | null>(null)

    if (!isEqual(nextKeys, keysRef.current)) {
      anchorRef.current = anchorTo
        ? null
        : resolveScrollAnchor({
            virtualizer: virtualizerRef.current,
            scrollElement: getScrollElement(),
            prevKeys: keysRef.current,
            nextKeys,
          })
      keysRef.current = nextKeys
    }

    const keys = keysRef.current
    const getItemKey = useCallback((index: number) => keys[index] ?? index, [keys])

    const directDom = directDomUpdates ?? !anchorTo
    const virtualizer = useVirtualizer<HTMLDivElement, Element>({
      count: items.length,
      getScrollElement,
      estimateSize: resolveSize,
      getItemKey,
      overscan,
      gap,
      paddingStart: (paddingStart ?? 0) + headerHeight,
      paddingEnd,
      anchorTo,
      followOnAppend,
      scrollEndThreshold,
      directDomUpdates: directDom,
      useFlushSync: flushSyncOnScroll,
      useAnimationFrameWithResizeObserver,
    })
    virtualizerRef.current = virtualizer

    const virtualItems = virtualizer.getVirtualItems()
    const totalSize = virtualizer.getTotalSize()
    const contentRef = useRef<HTMLDivElement | null>(null)
    const setContainerRef = useCallback(
      (node: HTMLDivElement | null) => {
        contentRef.current = node

        if (directDom) virtualizer.containerRef(node)
      },
      [directDom, virtualizer]
    )

    const stickToEnd = anchorTo === 'end'
    const wasAtEnd = useRef(stickToEnd)

    useImperativeHandle(
      ref,
      () => ({
        scrollToOffset: (offset, options) => {
          wasAtEnd.current = false
          virtualizer.scrollToOffset(offset, options)
        },
        scrollToIndex: (index, options) => {
          wasAtEnd.current = false
          virtualizer.scrollToIndex(index, options)
        },
        scrollToEnd: (options) => {
          wasAtEnd.current = true
          virtualizer.scrollToEnd(options)
        },
        isAtEnd: (threshold) => virtualizer.isAtEnd(threshold),
        getDistanceFromEnd: () => virtualizer.getDistanceFromEnd(),
        getScrollElement,
        getVirtualizer: () => virtualizer,
      }),
      [virtualizer, getScrollElement]
    )

    useLayoutEffect(() => {
      const anchor = anchorRef.current
      const scrollElement = getScrollElement()

      if (!anchor || !scrollElement) return

      anchorRef.current = null

      const start = virtualizer.measurementsCache[anchor.index]?.start

      if (start === undefined) return

      scrollElement.scrollTop = start + anchor.offset
    }, [keys])

    const firedKey = useRef<string | null>(null)
    const firedStartKey = useRef<number | null>(null)
    const itemsLength = useRef(items.length)

    useEffect(() => {
      if (!onEndReached || !items.length) return

      const last = virtualItems[virtualItems.length - 1]
      const nearEnd = !!last && last.index >= items.length - 1 - endReachedThreshold

      if (!nearEnd) {
        firedKey.current = null

        return
      }

      const key = `${items.length}:${virtualizer.scrollOffset}`

      if (firedKey.current === key) return

      firedKey.current = key
      onEndReached()
    }, [virtualItems, items.length, endReachedThreshold, onEndReached, virtualizer])

    useEffect(() => {
      itemsLength.current = items.length

      if (!onStartReached || !items.length) return

      const scrollOffset = getScrollElement()?.scrollTop
      const visibleStart =
        scrollOffset === undefined ? undefined : virtualizer.getVirtualItemForOffset(scrollOffset)
      const nearStart = !!visibleStart && visibleStart.index <= startReachedThreshold

      if (!nearStart) {
        firedStartKey.current = null

        return
      }

      if (firedStartKey.current === items.length) return

      const requestedLength = items.length
      const allowRetry = () => {
        if (itemsLength.current === requestedLength) firedStartKey.current = null
      }

      firedStartKey.current = requestedLength

      const pending = onStartReached()

      if (pending) pending.then(allowRetry, allowRetry)
    }, [virtualItems, items.length, startReachedThreshold, onStartReached])

    useEffect(() => {
      const el = headerRef.current
      if (!el) {
        setHeaderHeight(0)
        return
      }
      const observer = new ResizeObserver(() => setHeaderHeight(el.offsetHeight))
      observer.observe(el)
      setHeaderHeight(el.offsetHeight)

      return () => observer.disconnect()
    }, [header])

    useLayoutEffect(() => {
      if (!stickToEnd) return

      wasAtEnd.current = true
      virtualizer.scrollToEnd()
    }, [stickToEnd, virtualizer])

    useEffect(() => {
      if (stickToEnd && wasAtEnd.current) virtualizer.scrollToEnd()
    }, [stickToEnd, virtualizer, headerHeight, paddingStart, paddingEnd, totalSize])

    useEffect(() => {
      if (!stickToEnd) return

      const scroll = getScrollElement()

      if (!scroll) return

      let lastScrollTop = scroll.scrollTop
      const readPinned = () => {
        const { scrollHeight, clientHeight, scrollTop } = scroll

        if (scrollHeight - clientHeight - scrollTop <= (scrollEndThreshold ?? 0)) {
          wasAtEnd.current = true
        } else if (scrollTop < lastScrollTop) {
          wasAtEnd.current = false
        }
        lastScrollTop = scrollTop
      }

      virtualizer.shouldAdjustScrollPositionOnItemSizeChange = (item, delta, instance) =>
        wasAtEnd.current || item.start < (instance.scrollOffset ?? 0)

      const sizeObserver = new ResizeObserver(() => {
        if (wasAtEnd.current) virtualizer.scrollToEnd()
      })

      scroll.addEventListener('scroll', readPinned, { passive: true })
      sizeObserver.observe(scroll)
      if (contentRef.current) sizeObserver.observe(contentRef.current)

      return () => {
        virtualizer.shouldAdjustScrollPositionOnItemSizeChange = undefined
        scroll.removeEventListener('scroll', readPinned)
        sizeObserver.disconnect()
      }
    }, [stickToEnd, scrollEndThreshold, getScrollElement, virtualizer])

    const sizedContainer = (
      <div
        ref={setContainerRef}
        className={classNames(styles.inner, contentClassName)}
        style={directDom ? undefined : { height: virtualizer.getTotalSize() }}
        aria-label={getAriaLabel('VirtualList', 'content')}
      >
        {header && (
          <div ref={headerRef} className={styles.header}>
            {header}
          </div>
        )}
        {virtualItems.map((virtualItem) => {
          const child = items[virtualItem.index]
          const childKey = isValidElement(child) ? child.key : null
          const asSkeleton = skeletonWhileScrolling && virtualizer.isScrolling

          return (
            <div
              key={childKey ?? virtualItem.index}
              data-index={virtualItem.index}
              ref={measureItems && !asSkeleton ? virtualizer.measureElement : undefined}
              className={styles.item}
              style={
                directDom ? undefined : { transform: `translate3d(0, ${virtualItem.start}px, 0)` }
              }
            >
              {asSkeleton
                ? renderSkeleton?.(virtualItem.index) ?? (
                    <div className={styles.skeleton} style={{ height: virtualItem.size }} />
                  )
                : child}
            </div>
          )
        })}
      </div>
    )

    const content = (
      <>
        {contentWrapperClassName ? (
          <div className={contentWrapperClassName}>{sizedContainer}</div>
        ) : (
          sizedContainer
        )}
        {footer}
      </>
    )

    if (externalScroll) return content

    return (
      <div
        ref={ownScrollRef}
        className={classNames(styles.scroll, className)}
        style={style}
        onScroll={onScroll}
        aria-label={getAriaLabel('VirtualList', ariaLabel)}
      >
        {content}
      </div>
    )
  }
)

VirtualList.displayName = 'VirtualList'
